import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileDown, Send, Plus, Trash2 } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { QUOTE_STATUS, tl } from "@/lib/utils-tr";
import { toast } from "sonner";

export function AdminQuotes() {
  const [data, setData] = useState({ items: [] });
  const [status, setStatus] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    const qs = new URLSearchParams();
    if (status !== "all") qs.set("status", status);
    api.get(`/admin/quotes?${qs}`).then((r) => setData(r.data)).catch(() => {});
  }, [status]);

  return (
    <div>
      <h1 className="font-head text-2xl font-bold text-slate-900 mb-6">Teklif Talepleri</h1>
      <div className="mb-5 w-56">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="quote-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="yeni">Yeni Talep</SelectItem>
            <SelectItem value="fiyatlandiriliyor">Fiyatlandırılıyor</SelectItem>
            <SelectItem value="gonderildi">Gönderildi</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="bg-white border rounded-xl divide-y" data-testid="admin-quotes-list">
        {data.items.length === 0 && <div className="p-10 text-center text-slate-400">Talep yok.</div>}
        {data.items.map((q) => (
          <button key={q.quote_no} onClick={() => navigate(`/yonetim/teklifler/${q.quote_no}`)}
            data-testid={`admin-quote-${q.quote_no}`}
            className="w-full text-left p-4 hover:bg-slate-50 flex items-center justify-between gap-3">
            <div>
              <div className="font-mono font-semibold text-slate-900">{q.quote_no}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {q.musteri?.ad} {q.musteri?.firma ? `· ${q.musteri.firma}` : ""} · {(q.created_at || "").slice(0, 10)}
              </div>
            </div>
            <Badge variant="outline" className={QUOTE_STATUS[q.status]?.cls}>{QUOTE_STATUS[q.status]?.label}</Badge>
          </button>
        ))}
      </div>
    </div>
  );
}

const VAT_OPTS = [0, 0.01, 0.10, 0.20];

export function LegacyQuoteDetail() {
  const { quote_no } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [rows, setRows] = useState([]);
  const [kurlar, setKurlar] = useState({ USD_TRY: "", EUR_TRY: "" });
  const [extra, setExtra] = useState({ iskonto_tl: 0, nakliye_tl: 0, gecerlilik: "", odeme_sekli: "", teslim_suresi: "", sevkiyat_yontemi: "", sartlar: "" });
  const [calc, setCalc] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/admin/quotes/${quote_no}`).then((r) => {
      const data = r.data;
      setQ(data);
      setKurlar({ USD_TRY: data.kurlar?.USD_TRY ?? "", EUR_TRY: data.kurlar?.EUR_TRY ?? "" });
      setExtra({
        iskonto_tl: data.iskonto_tl || 0, nakliye_tl: data.nakliye_tl || 0,
        gecerlilik: data.gecerlilik || "", odeme_sekli: data.odeme_sekli || "",
        teslim_suresi: data.teslim_suresi || "", sevkiyat_yontemi: data.sevkiyat_yontemi || "",
        sartlar: data.sartlar || "",
      });
      setCalc(data.hesaplama || null);
      if (data.revizyon_items?.length) {
        setRows(data.revizyon_items);
      } else {
        // seed from original items + cost info
        setRows(data.original_items.map((it) => {
          const mi = it.maliyet_bilgisi || {};
          return {
            urun_id: it.urun_id, urun_kodu: it.urun_kodu, urun_adi: it.urun_adi, marka: it.marka,
            ambalaj: it.ambalaj, adet: it.adet,
            maliyet: mi.manuel_ambalaj_maliyeti ?? mi.ambalaj_adet_fiyati ?? null,
            para_birimi: mi.para_birimi || "TRY", kdv_orani: mi.kdv_orani ?? 0.20,
            kar_orani: null, manuel_birim_fiyat: null,
            uygunluk_ok: false,  // henüz değerlendirilmedi; yetkili her kalemi ayrıca onaylar
            aciklama: "",
          };
        }));
      }
    });
  };
  useEffect(load, [quote_no]);

  const setRow = (i, patch) => setRows((r) => r.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const addRow = () => setRows((r) => [...r, { urun_id: "", urun_kodu: "", urun_adi: "Alternatif ürün", marka: "", ambalaj: "", adet: 1, maliyet: null, para_birimi: "TRY", kdv_orani: 0.20, kar_orani: null, manuel_birim_fiyat: null, uygunluk_ok: true, aciklama: "" }]);

  const payload = () => ({
    revizyon_items: rows.map((x) => ({
      ...x,
      adet: parseInt(x.adet) || 0,
      maliyet: x.maliyet === "" || x.maliyet === null ? null : parseFloat(x.maliyet),
      kar_orani: x.kar_orani === "" || x.kar_orani === null ? null : parseFloat(x.kar_orani),
      manuel_birim_fiyat: x.manuel_birim_fiyat === "" || x.manuel_birim_fiyat === null ? null : parseFloat(x.manuel_birim_fiyat),
      kdv_orani: parseFloat(x.kdv_orani),
    })),
    kurlar: { USD_TRY: kurlar.USD_TRY === "" ? null : parseFloat(kurlar.USD_TRY), EUR_TRY: kurlar.EUR_TRY === "" ? null : parseFloat(kurlar.EUR_TRY) },
    iskonto_tl: parseFloat(extra.iskonto_tl) || 0, nakliye_tl: parseFloat(extra.nakliye_tl) || 0,
    gecerlilik: extra.gecerlilik, odeme_sekli: extra.odeme_sekli, teslim_suresi: extra.teslim_suresi,
    sevkiyat_yontemi: extra.sevkiyat_yontemi, sartlar: extra.sartlar,
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/quotes/${quote_no}`, payload());
      setCalc(data.hesaplama);
      toast.success("Teklif kaydedildi ve hesaplandı");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const send = async () => {
    try {
      await api.post(`/admin/quotes/${quote_no}/send`);
      toast.success("Teklif müşteriye gönderildi");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!q) return <div className="text-slate-400">Yükleniyor...</div>;
  const m = q.musteri || {};

  return (
    <div>
      <button onClick={() => navigate("/yonetim/teklifler")} className="text-sm text-slate-500 hover:text-teal-600 flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4" /> Teklif Talepleri
      </button>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-head text-2xl font-bold text-slate-900">{q.quote_no}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {m.ad} · {m.email} · {m.telefon} · Talep türü: <b>{m.talep_turu}</b>
            {m.firma ? ` · ${m.firma}` : ""}
          </p>
        </div>
        <Badge variant="outline" className={QUOTE_STATUS[q.status]?.cls}>{QUOTE_STATUS[q.status]?.label}</Badge>
      </div>

      {m.genel_not && <div className="bg-slate-50 border rounded-lg p-3 text-sm text-slate-600 mb-4">Müşteri notu: {m.genel_not}</div>}
      {q.okuma_beyani && (
        <div className="text-xs text-slate-500 mb-4" data-testid="admin-okuma-beyani">
          Okuma beyanı: {(q.okuma_beyani.tarih || "").replace("T", " ").slice(0, 19)} · Aydınlatma v{q.okuma_beyani.metin_surumleri?.aydinlatma?.surum}
          {" "}· Gizlilik v{q.okuma_beyani.metin_surumleri?.gizlilik?.surum} (açık rıza / pazarlama izni değildir)
        </div>
      )}
      {q.serbest_talepler?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm mb-4">
          <b>Serbest talepler:</b> {q.serbest_talepler.map((f) => f.metin).join(" | ")}
        </div>
      )}

      {/* Pricing table */}
      <div className="bg-white border rounded-xl p-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="pricing-table">
          <thead><tr className="text-left text-slate-500 border-b text-xs">
            <th className="py-2 pr-2">Ürün</th><th className="py-2 px-1">Adet</th>
            <th className="py-2 px-1">Maliyet</th><th className="py-2 px-1">PB</th>
            <th className="py-2 px-1">Kâr %</th><th className="py-2 px-1">Manuel Fiyat</th>
            <th className="py-2 px-1">KDV</th><th className="py-2 px-1">Değerlendirildi</th>
            <th className="py-2 px-1 text-right">Satır (TL)</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map((x, i) => (
              <tr key={i} className="border-b" data-testid={`pricing-row-${i}`}>
                <td className="py-2 pr-2 min-w-[180px]">
                  <Input value={x.urun_adi} onChange={(e) => setRow(i, { urun_adi: e.target.value })} className="h-8 text-xs" />
                  <div className="text-[10px] text-slate-400 mt-0.5">{x.urun_kodu} {x.ambalaj}</div>
                </td>
                <td className="py-2 px-1"><Input type="number" value={x.adet} onChange={(e) => setRow(i, { adet: e.target.value })} className="h-8 w-14 text-xs" data-testid={`row-adet-${i}`} /></td>
                <td className="py-2 px-1"><Input type="number" value={x.maliyet ?? ""} onChange={(e) => setRow(i, { maliyet: e.target.value })} className="h-8 w-20 text-xs" data-testid={`row-maliyet-${i}`} /></td>
                <td className="py-2 px-1">
                  <Select value={x.para_birimi} onValueChange={(v) => setRow(i, { para_birimi: v })}>
                    <SelectTrigger className="h-8 w-20 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="TRY">TRY</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem></SelectContent>
                  </Select>
                </td>
                <td className="py-2 px-1"><Input type="number" value={x.kar_orani ?? ""} onChange={(e) => setRow(i, { kar_orani: e.target.value })} className="h-8 w-16 text-xs" placeholder="%" data-testid={`row-kar-${i}`} /></td>
                <td className="py-2 px-1"><Input type="number" value={x.manuel_birim_fiyat ?? ""} onChange={(e) => setRow(i, { manuel_birim_fiyat: e.target.value })} className="h-8 w-20 text-xs" data-testid={`row-manuel-${i}`} /></td>
                <td className="py-2 px-1">
                  <Select value={String(x.kdv_orani)} onValueChange={(v) => setRow(i, { kdv_orani: parseFloat(v) })}>
                    <SelectTrigger className="h-8 w-16 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{VAT_OPTS.map((o) => <SelectItem key={o} value={String(o)}>%{o * 100}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="py-2 px-1 text-center">
                  <input type="checkbox" checked={x.uygunluk_ok} onChange={(e) => setRow(i, { uygunluk_ok: e.target.checked })} data-testid={`row-uygun-${i}`} />
                </td>
                <td className="py-2 px-1 text-right font-medium">{x.satir_tl != null ? tl(x.satir_tl) : "—"}</td>
                <td className="py-2 pl-1"><button onClick={() => removeRow(i)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <Button variant="outline" size="sm" className="mt-3" onClick={addRow} data-testid="add-row-btn">
          <Plus className="w-4 h-4 mr-1" /> Alternatif Ürün Ekle
        </Button>
      </div>

      {/* Rates + extras */}
      <div className="grid md:grid-cols-2 gap-5 mt-5">
        <div className="bg-white border rounded-xl p-4 space-y-3">
          <h3 className="font-head font-semibold text-slate-900">Döviz Kurları (manuel)</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">USD/TL</Label><Input type="number" value={kurlar.USD_TRY} onChange={(e) => setKurlar({ ...kurlar, USD_TRY: e.target.value })} className="mt-1" data-testid="usd-rate" /></div>
            <div><Label className="text-xs">EUR/TL</Label><Input type="number" value={kurlar.EUR_TRY} onChange={(e) => setKurlar({ ...kurlar, EUR_TRY: e.target.value })} className="mt-1" data-testid="eur-rate" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">İskonto (TL)</Label><Input type="number" value={extra.iskonto_tl} onChange={(e) => setExtra({ ...extra, iskonto_tl: e.target.value })} className="mt-1" data-testid="iskonto" /></div>
            <div><Label className="text-xs">Nakliye (TL)</Label><Input type="number" value={extra.nakliye_tl} onChange={(e) => setExtra({ ...extra, nakliye_tl: e.target.value })} className="mt-1" data-testid="nakliye" /></div>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4 space-y-2">
          <h3 className="font-head font-semibold text-slate-900">Teklif Şartları</h3>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Geçerlilik" value={extra.gecerlilik} onChange={(e) => setExtra({ ...extra, gecerlilik: e.target.value })} data-testid="gecerlilik" />
            <Input placeholder="Ödeme şekli" value={extra.odeme_sekli} onChange={(e) => setExtra({ ...extra, odeme_sekli: e.target.value })} data-testid="odeme" />
            <Input placeholder="Teslim süresi" value={extra.teslim_suresi} onChange={(e) => setExtra({ ...extra, teslim_suresi: e.target.value })} data-testid="teslim" />
            <Input placeholder="Sevkiyat yöntemi" value={extra.sevkiyat_yontemi} onChange={(e) => setExtra({ ...extra, sevkiyat_yontemi: e.target.value })} data-testid="sevkiyat" />
          </div>
          <Textarea placeholder="Satış/teklif şartları" value={extra.sartlar} onChange={(e) => setExtra({ ...extra, sartlar: e.target.value })} data-testid="sartlar" />
        </div>
      </div>

      {/* Totals + actions */}
      <div className="flex flex-wrap items-center justify-between gap-4 mt-5 bg-slate-900 text-white rounded-xl p-5">
        <div className="flex gap-6 text-sm flex-wrap">
          <div><div className="text-slate-400 text-xs">Ara Toplam</div><div className="font-semibold">{calc ? tl(calc.ara_toplam_tl) : "—"}</div></div>
          <div><div className="text-slate-400 text-xs">KDV</div><div className="font-semibold">{calc ? tl(calc.kdv_tl) : "—"}</div></div>
          <div><div className="text-slate-400 text-xs">Genel Toplam</div><div className="font-bold text-teal-300 text-lg" data-testid="admin-grand-total">{calc ? tl(calc.genel_toplam_tl) : "—"}</div></div>
          {calc?.eksik_kur && <div className="text-amber-400 text-xs self-center">⚠ Kur eksik — toplam tamamlanmadı</div>}
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-pricing-btn">
            {saving ? "..." : "Kaydet & Hesapla"}</Button>
          <Button variant="outline" className="bg-transparent text-white border-white/30 hover:bg-white/10"
            onClick={() => window.open(`${API}/admin/quotes/${quote_no}/pdf`, "_blank")} data-testid="pdf-btn">
            <FileDown className="w-4 h-4 mr-1.5" /> PDF Önizle</Button>
          <Button onClick={send} className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-quote-btn">
            <Send className="w-4 h-4 mr-1.5" /> Müşteriye Gönder</Button>
        </div>
      </div>
    </div>
  );
}
